import { Component, Input } from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { NotifyService } from 'common/util/services/notify.service'
import { ElementsUser, OneTimeAccessToken } from 'elements-sdk'
import { IdentityService } from 'main/services/identity.service'

export interface ReadyLink {
  token: OneTimeAccessToken,
  recipient?: ElementsUser|string,
}

function emailFromRecipient (recipient: ElementsUser|string): string|null {
  if (typeof recipient === 'string') {
    return recipient
  }
  return recipient.email ?? null
}

function nameFromRecipient (recipient: ElementsUser|string|undefined): string {
  if (!recipient) {
    return 'Anonymous'
  }
  if (typeof recipient === 'string') {
    return recipient
  }
  return recipient.displayName
}

@Component({
  templateUrl: './sharingLinksReadyModal.component.pug',
})
export class SharingLinksReadyModalComponent {
  @Input() links: ReadyLink[]
  mailSubject = ''
  mailText = ''
  mailCount = 0
  customEmail = ''

  sendEmailsPlural = `Send {count, plural,
    =0{e-mail}
    one{e-mail}
    other{{count} e-mails}
  }`

  constructor (
    public modalInstance: NgbActiveModal,
    private translate: TranslateService,
    private api: APIService,
    private notify: NotifyService,
    private identity: IdentityService,
  ) { }

  async ngOnInit () {
    this.mailSubject = this.getMailSubjectForToken(this.links[0].token)
    this.mailCount = this.links.filter(x => x.recipient && emailFromRecipient(x.recipient)).length
  }

  getMailSubjectForToken (token: OneTimeAccessToken): string {
    const count = Math.max(0, token.mediaRootPermissions.length - 1)
    const author = this.identity.user?.displayName ?? this.translate.instant('Unknown')
    const item = token.sharedBundles.concat(token.sharedDirectories)[0]?.name
    return this.translate.instant(
      `{ author } has shared {item} {count, plural,
         =0{}
         one{and one other item}
         other{and {count} other items}} with you`,
      { author, item, count })
  }

  sendEmails = async () => {
    for (const link of this.links.slice()) {
      if ((typeof link.recipient !== 'string') && !link.recipient?.email && !this.customEmail) {
        this.notify.warning(
          this.translate.instant('One e-mail not sent'),
          this.translate.instant('User {name} has no e-mail address', { name: nameFromRecipient(link.recipient) }),
        )
      } else {
        await this.api.auth.sendAccessTokenEmailNotification({
          id: link.token.id,
          sendLinkEmailRequest: {
            email: (link.recipient && emailFromRecipient(link.recipient)) || this.customEmail,
            subject: this.mailSubject || undefined,
            text: this.mailText || undefined,
          },
        })
        this.links = this.links.filter(x => x !== link)
      }
    }
    this.notify.success(this.translate.instant('E-mail sent'))
    this.modalInstance.close()
  }

  onLinkDeleted (link: ReadyLink) {
    this.links = this.links.filter(x => x !== link)
    if (!this.links.length) {
      this.modalInstance.close()
    }
  }
}
